/**
 * 3x written exercise — for each first-pass-missed word, Eva physically
 * writes it three times on paper. The app shows the correct spelling,
 * speaks it, and tracks her progress with a single confirmation tap per
 * word. Implements spec §4.5.
 *
 * Rules from the spec:
 *   - The list is words missed on **first pass**, in first-pass order —
 *     **regardless of second-chance outcome.** (Same helper as second
 *     chance: `missedFromFirstPass`.)
 *   - The full correct spelling is displayed large and clearly. Audio
 *     auto-plays on word arrival; "Say it again" and optional
 *     "Hear sentence" buttons are available.
 *   - Pumpkin is not visible (task-focus screen, §7).
 *
 * Confirmation model: spec §4.5 originally specified three "I did it"
 * taps per word with a 1/3 → 3/3 indicator. In live QA we collapsed
 * that to a single confirmation tap — Eva's already writing on paper
 * three times; making her tap thrice for the same word is just extra
 * cognitive load. The stored counter still goes 0→3 in one move so the
 * spec §9 data model + /api/sessions wire format stay unchanged.
 *
 * After confirming the last word: navigate to celebration, which
 * persists the session and runs the achievement engine.
 */
import { useCallback, useEffect, useMemo } from "react";

import { Button } from "@/components/Button";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { cancelSpeech, speak } from "@/lib/speech";
import { missedFromFirstPass } from "@/lib/spellingCompare";
import "./ThreeXWriteScreen.css";

const COMPLETE_COUNT = 3;

export function ThreeXWriteScreen() {
  const { navigate } = useNavigation();
  const { session, markThreeXComplete } = useSession();

  // Stable per-session list — same shape as second chance.
  const missed = useMemo(() => {
    if (!session) return [];
    return missedFromFirstPass(session.words, session.firstPassAttempts);
  }, [session]);

  // Bounce on no session; skip to celebration if no misses (defensive —
  // ResultsScreen routes 10/10 directly to celebration, but second
  // chance could theoretically land us here with nothing to do).
  useEffect(() => {
    if (!session) {
      navigate("home");
      return;
    }
    if (missed.length === 0) navigate("celebration");
  }, [session, missed.length, navigate]);

  // Find the next word still needing confirmation. We walk the missed
  // list and pick the first one with completions < 3 — this is also
  // the spec §5.3 resume entry point for this screen, so a session
  // rehydrated from localStorage with some words already done lands
  // straight on the right next-up word.
  const activeIndex = useMemo(() => {
    if (!session) return -1;
    for (let i = 0; i < missed.length; i++) {
      const done = session.threeXCompletions[missed[i].id] ?? 0;
      if (done < COMPLETE_COUNT) return i;
    }
    return missed.length;
  }, [session, missed]);

  const word = activeIndex >= 0 && activeIndex < missed.length ? missed[activeIndex] : null;
  const wordId = word?.id;

  // Auto-speak each new word on arrival. Re-fires only when the word
  // identity changes — not on every tap.
  useEffect(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordId]);

  // When all words are done, advance to celebration.
  useEffect(() => {
    if (session && missed.length > 0 && activeIndex >= missed.length) {
      navigate("celebration");
    }
  }, [session, missed.length, activeIndex, navigate]);

  const handleSayAgain = useCallback(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
  }, [session, word]);

  const handleHearSentence = useCallback(() => {
    if (!session || !word || word.exampleSentence === null) return;
    speak(word.exampleSentence, session.settings);
  }, [session, word]);

  const handleIDidIt = useCallback(() => {
    if (!session || !word) return;
    markThreeXComplete(word.id);
  }, [session, word, markThreeXComplete]);

  if (!session || !word) return null;

  return (
    <main className="three-x">
      <div
        className="three-x__progress"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={missed.length}
        aria-valuenow={activeIndex + 1}
        aria-label={`Word ${activeIndex + 1} of ${missed.length}`}
      >
        {missed.map((w, i) => (
          <span
            key={w.id}
            className={
              "three-x__progress-dot" +
              (i < activeIndex ? " is-done" : "") +
              (i === activeIndex ? " is-current" : "")
            }
            aria-hidden="true"
          />
        ))}
      </div>

      <div className="three-x__body">
        <p className="three-x__instruction">
          Write this word 3 times on a piece of paper. Press the button below
          once you&rsquo;ve done it.
        </p>
        <p className="three-x__word">{word.spelling}</p>

        <div className="three-x__audio-row">
          <button
            type="button"
            className="three-x__audio-btn"
            onClick={handleSayAgain}
            aria-label="Hear the word again"
          >
            <span aria-hidden="true">🔊</span> Say it again
          </button>
          {word.exampleSentence !== null && (
            <button
              type="button"
              className="three-x__audio-btn"
              onClick={handleHearSentence}
              aria-label="Hear a sentence"
            >
              <span aria-hidden="true">♪</span> Hear a sentence
            </button>
          )}
        </div>

        <Button size="large" onClick={handleIDidIt}>
          I did it
        </Button>
      </div>
    </main>
  );
}
